/**
 * Domain types for Edit Chat functionality
 * Reuses existing types from createChat and chat modules
 */

import type { ChatType, ChatListItem } from './chat';
import type {
  BaseSelectOption,
  BuyerSelectOption,
  UserSelectOption,
  BuyerUserSelectOption,
  SupplierUserSelectOption,
  CreatedContextOption,
  DeliveryDay,
  CutOffTimeType,
  WeekdayBasedCutoffSettings,
  CreateChatFormData,
} from './createChat';

// GraphQL-compatible types matching the server schema
export type ChatStatus = 'active' | 'archived' | 'pending' | 'requested';
export type GraphQLCutOffTimeType = 'flexible' | 'noCutoff' | 'strict';
export type GraphQLChatType = 'ordering' | 'support';

// Edit Chat Tab Names
export enum EditChatTabNames {
  PARTICIPANTS = 'participants',
  SETTINGS = 'settings',
  ORDER_GUIDE = 'order-guide',
  CHAT_HISTORY = 'chat-history',
}

// Edit Chat Form Data (extends CreateChatFormData with additional fields for editing)
export interface EditChatFormData
  extends Omit<CreateChatFormData, 'cutoffType' | 'chatType'> {
  // Chat identification
  id: string;
  chatStatus: ChatStatus;

  // Override types to match GraphQL schema
  cutoffType: GraphQLCutOffTimeType;
  chatType: {
    value: GraphQLChatType;
    label: string;
  } | null;

  // Original data for comparison
  originalBuyerId?: string;
  originalSupplierId?: string;
  originalChatType?: ChatType;
  originalCreatedContext?: string;
}

// API input for updating chat
export interface UpdateChatInput {
  id: string;
  chatName?: string | null;
  restaurantName?: string | null;
  deliveryAddress?: string | null;
  customerNumber?: string | null;
  additionalEmails?: string[];
  buyerId?: string | null;
  subCatalogIds?: string[];
  enabled?: boolean;
}

// Chat details response from API (extends ChatListItem with additional edit-specific data)
export interface ChatDetailsResponse extends Omit<ChatListItem, 'type'> {
  status: ChatStatus;
  type: ChatType;
  createdAt: string;
  updatedAt: string;
  additionalEmails: string[];

  // Extended user information for editing
  users: Array<{
    id: string;
    name: string;
    email?: string;
    phone?: string;
    supplier: boolean;
    orderPreferences?: {
      whatsApp?: boolean;
      sms?: boolean;
      fax?: boolean;
    };
  }>;

  // Configuration
  subCatalogs?: Array<{
    id: string;
    name: string;
    supplierId: string;
  }>;
  priceVisibility?: {
    displayPricesOverride?: boolean;
  };
  deliverySettings?: {
    hasCustomSettings: boolean;
    deliveryDays?: DeliveryDay[];
    cutoffType?: GraphQLCutOffTimeType;
    cutoffTimes?: WeekdayBasedCutoffSettings;
  };
  movSettings?: {
    amount?: string;
    currency?: string;
  };
}

// Chat history types
export interface ChatHistoryMessage {
  id: string;
  dateTime: string;
  from: string;
  to: string;
  orderId?: string;
  message: string;
}

export interface ChatHistoryResponse {
  messages: ChatHistoryMessage[];
  totalCount: number;
}

// Hook configuration types (reuse pattern from createChat)
export interface EditChatConfig {
  onSuccess?: (chat: ChatDetailsResponse) => void;
  onError?: (error: Error) => void;
  captureException?: (error: Error) => void;
}

// Validation rules for edit chat (extends create chat validation concept)
export interface EditChatValidationRules {
  canEditBuyer: boolean;
  canEditSupplier: boolean;
  canEditChatType: boolean;
  canEditCreatedContext: boolean;
  customerNumberMandatory: boolean;
  requiresBuyerUsers: boolean;
  requiresSupplierUsers: boolean;
}

// Navigation and tab management
export interface EditChatTabConfig {
  name: EditChatTabNames;
  title: string;
  formFieldNames: string[];
  isEnabled: boolean;
  hasChanges: boolean;
  isValid: boolean;
}

// User management operations
export interface ChatUserOperation {
  type: 'add' | 'remove';
  userId: string;
  userType: 'buyer' | 'supplier';
}

// Duplicate chat functionality
export interface DuplicateChatOptions {
  chatId: string;
  includeParticipants: boolean;
  includeSettings: boolean;
  includeOrderGuide: boolean;
}

// Response type reuses existing pattern
export interface DuplicateChatResponse {
  id: string;
  chatName: string;
  status: ChatStatus;
}

// Additional form types for complete EditChat functionality
export interface EditChatFormFields {
  // Chat participants
  buyer: BuyerSelectOption | null;
  supplier: UserSelectOption | null;
  buyerUsers: BuyerUserSelectOption[] | null;
  supplierUsers: SupplierUserSelectOption[] | null;
  deselectedOrderPreferences?: string[];

  // Chat settings
  chatName?: string | null;
  deliveryAddress?: string | null;
  restaurantName?: string | null;
  customerNumber?: string | null;
  chatType: BaseSelectOption | null;
  customerLevelMovAmount?: string | null;
  customerLevelMovCurrency?: string | null;
  additionalEmails: string;
  subCatalog: BaseSelectOption[] | null;
  createdContext: CreatedContextOption | null;
  visibleChatPrices?: boolean;
  isPriceOverrideEnabled?: boolean;

  // Edit-specific fields
  isPuppy?: boolean | null;

  // Delivery settings
  deliveryDays?: DeliveryDay[];
  cutoffType?: CutOffTimeType;
  cutoffTimes?: WeekdayBasedCutoffSettings;
}

// Order preference types
export enum FormattedOrderPreferenceTypesEnum {
  WHATSAPP = 'WHATSAPP',
  SMS = 'SMS',
  FAX = 'FAX',
}

export interface FormattedOrderPreference {
  type: FormattedOrderPreferenceTypesEnum;
  value: string;
}

// User metadata for select options
export interface UserSelectOptionMetaData {
  name: string | null | undefined;
  supplierId: string | null | undefined;
  phone: string | null | undefined;
  email: string | null | undefined;
  orderPreference: any | null | undefined; // OrderPreferenceFragment
  formattedOrderPreference: FormattedOrderPreference[] | undefined;
  customerNumberMandatory: boolean | null | undefined;
}

export interface BuyerSelectOptionMetaData {
  fullAddress: string;
}

export interface SupplierUserSelectOptionMetaData {
  supplier: boolean;
  phone: string;
  formattedOrderPreference: FormattedOrderPreference[];
  email: string | undefined | null;
}

export interface BuyerUserSelectOptionMetaData {
  supplier: boolean;
  phone: string;
}

// Tab panel configuration
export interface EditChatTabPanelConfig {
  name: EditChatTabNames;
  title: string;
  formFieldNames: string[];
  width?: string;
}

// Form validation
export interface EditChatValidationConfig {
  chatStatus: ChatStatus;
  customerNumberMandatory: boolean;
}

// Product-related types for Order Guide tab
export interface ChatProduct {
  id: string;
  name: string;
  unit: string;
  par?: number | null;
  externalId?: string | null;
  price?: {
    amount: number;
    currency: string;
    decimalPlaces: number;
  } | null;
}

export interface ProductUpdateInput {
  chatId?: string;
  id: string;
  fieldName: string;
  fieldValue: string;
}

// Chat history types
export interface ChatHistoryOrder {
  id: string;
  status: string;
  total?: {
    amount: number;
    currency: string;
  };
  createdAt: string;
}

export interface ChatHistoryMessageData extends ChatHistoryMessage {
  orderDetails?: ChatHistoryOrder;
}

// Delivery settings
export interface DeliverySettingFragment {
  deliveryDays?: DeliveryDay[];
  cutoffType?: CutOffTimeType;
  cutoffTimes?: WeekdayBasedCutoffSettings;
  hasCustomSettings?: boolean;
}

// Context providers
export interface ChatPageContextData {
  pageErrorFields: string[];
  setPageErrorFields: (fields: string[]) => void;
}

export interface DeliverySettingContextData {
  chatDeliverySetting: DeliverySettingFragment | null;
  updateDeliverySetting: (setting: DeliverySettingFragment) => void;
}

// Wizard context for create chat flow
export interface WizardContextData {
  hasSupplierEntity: boolean;
  setHasSupplierEntity: (has: boolean) => void;
  createdContext?: any;
}

// API operation types
export interface ChatUserOperationInput {
  chatId: string;
  userId: string;
  userType: 'buyer' | 'supplier';
  operation: 'add' | 'remove';
}

export interface SubCatalogOperationInput {
  chatId: string;
  subCatalogIds: string[];
  operation: 'add' | 'remove';
}

export interface UpdateChatPricesVisibilityInput {
  chatId: string;
  displayPricesOverride: boolean;
}

export interface UpdateNotificationPreferencesInput {
  chatId: string;
  userId: string;
  preferences: {
    whatsApp?: boolean;
    sms?: boolean;
    fax?: boolean;
  };
}

// MOV (Minimum Order Value) settings
export interface SupplierBuyerMovInput {
  supplierId: string;
  buyerId: string;
  amount: string;
  currency: string;
}

export interface RemoveSupplierBuyerMovInput {
  supplierId: string;
  buyerId: string;
}
